import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { cloneDeep, get, isEmpty, isEqual, isNil, omit, pick, snakeCase, template } from 'lodash';

import { PromptExit, navigate } from 'libs/router';

import { Form } from 'components/form';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import PageHeading from 'components/page/page-heading/PageHeading';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { withModal } from 'components/modal/ModalHOC';
import OfferTypeForm from 'modules/subcampaigns/components/forms/offer-type-form/OfferTypeForm';
import OfferAdditionalCompensation from 'modules/subcampaigns/components/forms/offer-additional-compensation/OfferAdditionalCompensation';
import OfferShippingForm from 'modules/subcampaigns/components/forms/offer-shipping-form/OfferShippingForm';
import OfferLegalRequirementsForm from 'modules/subcampaigns/components/forms/offer-legal-requirements-form/OfferLegalRequirementsForm';
import OfferCardBanner from '../offer-card-banner/OfferCardBanner';
import OfferPersonalMessageForm from 'modules/subcampaigns/components/forms/offer-personal-message/OfferPersonalMessageForm';
import BulkCustomizeOfferModal from 'modules/subcampaigns/components/bulk/bulk-customize-offer-modal/BulkCustomizeOfferModal';
import PageSection from 'components/page/page-section/PageSection';
import SubcampaignResizeControl from './SubcampaignResizeControl';
import FormRequiredMark from 'components/form/form-required-mark/FormRequiredMark';
import DeliverableEdit from 'modules/deliverables/components/deliverable-edit/DeliverableEdit';

import { putOffer, putOfferState, saveOffersAsDraft } from 'modules/subcampaigns/reducers/offer';
import {
  fetchSubcampaigns,
  resetSubcampaigns,
  uncancelSubcampaign
} from 'modules/campaigns/reducers/campaignSubcampaigns';
import {
  performBulkOfferCreation,
  resetBulkOffer,
  setBulkOfferContentTypes
} from 'modules/subcampaigns/reducers/bulkOffer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { danger } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';
import { isInfluencerRealm } from 'utils/account';
import { updateQueryParams } from 'utils/location';
import { DISABLED_OUTFIT_STATUSES } from 'utils/realm-outfits';
import {
  SUBCAMPAIGN_OFFER_TYPE_OFFER,
  SUBCAMPAIGN_OFFER_TYPE_QUOTE,
  SUBCAMPAIGN_STATE_PENDING,
  TASK_TAGS_COLORS,
  TASK_TAGS_COLORS_REVERSE,
  isAccepted,
  isCanceled,
  isDraft,
  isExpired,
  isOfferDraftAndBlank,
  isQuoteProvided,
  isRevoked
} from 'utils/subcampaign';
import { getPriceRecommendation } from 'utils/influencers';
import { getPlainText } from 'utils/editor';
import { logError } from 'utils/log';
import { dataCommonToComponent, dataGetCommonStructure } from 'utils/data/data';
import { API_CODE_TASK_CONTAINS_BINDING_OUTFITS } from 'utils/status';
import { convertListToHumanReadable } from 'utils/helper/common';
import {
  getBulkOfferSelectedSubcampaigns,
  getCustomOfferFields
} from 'utils/bulk-offer';
import {
  DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER,
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  canAddDeliverableToSubcampaigns,
  filterOutIrrelevantDeliverables,
  generateNewDeliverableId,
  getCampaignContentType,
  getInfluencerAvailableDeliverableChannels,
  getInfluencersAvailableDeliverableChannels,
  getUniqDeliverableTypes,
  sortDeliverables,
  sortNewDeliverables
} from 'utils/deliverables';
import { scrollToMissingField } from 'utils/page';
import {
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
} from 'utils/channels';
import { setActionByName } from 'utils/prompt-exit';

import type {
  BrandProfile,
  Campaign,
  CampaignOverview,
  Deliverable as DeliverableAlias,
  DeliverableType,
  Dispatch,
  LocationWindow,
  MixedSelectDropdownItem,
  Subcampaign,
  SubcampaignBulkOfferStore,
  SubcampaignInfluencerProfile,
  SubcampaignOffer,
  SubcampaignOfferDetailsBuffer,
  SubcampaignOfferForm,
  SubcampaignOfferState,
  SubcampaignOfferType
} from 'types';
import type { OfferAdditionalCompensationData } from '../forms/offer-additional-compensation/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./subcampaign-offer.i18n.json'));
applyTranslations(require('../copy/subcampaign-offer-copy-paste.i18n.json'));

type OwnProps = {
  subcampaign: Subcampaign,
  brandProfile: BrandProfile,
  // eslint-disable-next-line react/no-unused-prop-types
  isBulkOfferAllowed?: boolean, // used in mapStateToProps,
  campaignOverview: CampaignOverview,
  campaign: Campaign,
  offer: SubcampaignOffer,
  offerDeliverables: Array<DeliverableAlias>,
  // eslint-disable-next-line react/no-unused-prop-types
  isMultipleCreatorsSelected?: boolean // used in getDefaultState
};

type StateProps = {
  isPasteOfferActive: boolean,
  isBulkOfferActive: boolean,
  bulkOfferStore: SubcampaignBulkOfferStore,
  copiedOfferDetails: SubcampaignOfferDetailsBuffer | undefined | null,
  location: LocationWindow,
  subcampaignsList: Array<Subcampaign>,
  variablePaymentsAmount: number,
  overview: CampaignOverview,
  isSCP: boolean
};

type DispatchProps = {
  dispatchPutOffer: (b: SubcampaignOfferForm, a: Array<DeliverableAlias>) => Promise<void>,
  dispatchSaveOffersAsDraft: (o: Array<SubcampaignOfferForm>, d: Array<DeliverableAlias>) => Promise<void>,
  dispatchPutOfferState: (b: number, a: SubcampaignOfferState) => Promise<void>,
  dispatchUncancelSubcampaign: (a: Subcampaign) => Promise<void>,
  dispatchPerformBulkOfferCreation: (
    d: number,
    c: Array<number>,
    b: SubcampaignOfferForm,
    a: Array<DeliverableAlias>
  ) => Promise<void>,
  dispatchFetchSubcampaigns: (a: number) => Promise<void>,
  dispatchResetSubcampaigns: () => Promise<void>,
  dispatchSetContentTypes: (a: Array<string>) => Promise<void>,
  dispatchResetBulkOffer: () => void,
  dispatch: Dispatch
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  offer: SubcampaignOfferForm,
  deliverables: Array<DeliverableAlias>,
  showValidation: boolean,
  shouldPersistChangesOnBackend: boolean,
  needsRenegotiations: boolean,
  isModified: boolean,
  isSendingOffer: boolean,
  controlledComponentRerenderKey: string,
  isValidTermsAndConditions: boolean,
  isValidTypeOfOffer: boolean,
  expandedIdsMap: {
    [x: string]: boolean
  },
  tags: Array<MixedSelectDropdownItem>
};

const DELIVERABLE_SORTING = 'start_date_asc';

const EMPTY_OFFER_TYPE = {
  type: null,
  perks: null,
  budget: null
};

const ActionButtonWithBulkCustomizeOffer = withModal(ActionButton, {
  modalContent: BulkCustomizeOfferModal
});

const CONFIRMATION_DESCRIPTION = __t('subcampaign_offer_module.send_initial_offer_confirmation_description');

const headerHeight = 90;

class SubcampaignOfferEdit extends React.Component<Props, State> {
  initialState: Partial<State>;
  form: Form | undefined | null = null;
  promptExitActionNameForSaveAsDraft = 'save-as-draft';
  headerRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: Props) {
    super(props);

    const { offerDeliverables } = props;

    this.state = {
      ...this.getDefaultState(props),
      isSendingOffer: false,
      expandedIdsMap: {},
      tags: this.getTags(offerDeliverables)
    };

    this.initialState = this.pickOfferStatePart();
  }

  async componentDidMount(): Promise<void> {
    const { isPasteOfferActive, subcampaign, offerDeliverables, dispatchFetchSubcampaigns } = this.props;

    // fetching subcampaigns to perform the check `canAddDeliverableToSubcampaigns()`
    await dispatchFetchSubcampaigns(subcampaign.campaign.id);

    if (isPasteOfferActive) {
      this.pasteCopiedOffer();
    }

    this.setOfferTypeForBulkEdit();
    this.setActionForAdditionalPromptExit();
    const headerRef = this.headerRef && this.headerRef.current;

    if (headerRef) {
      window.scrollTo({
        top: headerRef.offsetTop + headerHeight,
        left: 0,
        behavior: 'smooth'
      });
    }

    this.setState({
      expandedIdsMap: offerDeliverables.reduce((prevValue, deliverable) => {
        // eslint-disable-next-line no-param-reassign
        prevValue[String(deliverable.id)] = false;

        return prevValue;
      }, {})
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { subcampaign, offerDeliverables, isPasteOfferActive, offer } = this.props;
    const { isModified } = this.state;

    this.setActionForAdditionalPromptExit();

    if (prevProps.isPasteOfferActive !== isPasteOfferActive && isPasteOfferActive) {
      this.pasteCopiedOffer();
      return;
    }

    const notEqualOffers = !isEqual(prevProps.offer, offer);

    if (!isModified && !prevState.isModified || !isEqual(subcampaign, prevProps.subcampaign)) {
      this.setState(state => ({ // eslint-disable-line react/no-did-update-set-state
        ...state,
        ...this.getDefaultState(this.props),
        controlledComponentRerenderKey: (notEqualOffers) ? Date.now().toString() : state.controlledComponentRerenderKey
      }));
      return;
    }

    if (notEqualOffers) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ controlledComponentRerenderKey: Date.now().toString() });
    }

    if (offerDeliverables !== prevProps.offerDeliverables) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        deliverables: offerDeliverables || []
      });
    }
  }

  componentWillUnmount(): void {
    const { dispatchSetContentTypes, dispatchResetSubcampaigns } = this.props;

    dispatchSetContentTypes([]);
    dispatchResetSubcampaigns();
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !isEqual(this.props, nextProps)
      || !isEqual(this.state, nextState);
  }

  getDisabledOutfitStatusString = (): string => (
    convertListToHumanReadable(DISABLED_OUTFIT_STATUSES)
  );

  getErrorMsgByCode = (
    error: any
  ): string => {
    const errorCode = error?.response?.data?.code;
    const errorMsgByCode = __t(`subcampaign_offer_module.errors.${errorCode}`);
    let errorMsg = errorMsgByCode || __t('subcampaign_offer_module.errors.bulk_offer');

    if (errorCode === API_CODE_TASK_CONTAINS_BINDING_OUTFITS) {
      errorMsg = template(errorMsg)({
        outfitStatuses: this.getDisabledOutfitStatusString()
      });
    }

    return errorMsg;
  };

  getTags(updatedDeliverables?: Array<DeliverableAlias>): Array<MixedSelectDropdownItem> {
    let deliverables = [] as Array<DeliverableAlias>;
    const addedTags = [] as Array<string>;

    if (!updatedDeliverables && this.state) {
      deliverables = this.state.deliverables;
    }

    return (updatedDeliverables || deliverables).reduce((tags, currentDeliverable, index) => {
      if (currentDeliverable.tag?.name && !addedTags.includes(currentDeliverable.tag.name)) {
        addedTags.push(currentDeliverable.tag.name);

        tags.push({
          id: index,
          identifier: `${index}-${currentDeliverable.tag.name}`,
          label: currentDeliverable.tag.name,
          isSelected: false,
          isDisabled: false,
          isFocused: false,
          value: currentDeliverable.tag.name,
          color: TASK_TAGS_COLORS[currentDeliverable.tag.color || '']
        });
      }

      return tags;
    }, [] as Array<MixedSelectDropdownItem>);
  }

  getFilteredDeliverables = (): Array<DeliverableAlias> => {
    const { tags, deliverables } = this.state;

    const selectedTags = tags.reduce((tagsStr, currentTag) => {
      if (currentTag.isSelected && currentTag.color) {
        tagsStr.push(`${currentTag.value}-${TASK_TAGS_COLORS_REVERSE[currentTag.color]}`);
      }

      return tagsStr;
    }, [] as Array<string>);

    let filteredDeliverables = deliverables;

    if (selectedTags.length) {
      filteredDeliverables = deliverables.filter(deliverable =>
        deliverable.tag?.name && selectedTags.includes(`${deliverable.tag.name}-${deliverable.tag.color}`));
    }

    return filteredDeliverables;
  };

  handleTagFilterChange = (updatedItems): void => {
    this.setState((prevState) => ({
      ...prevState,
      tags: updatedItems
    }));
  };

  handleRemoveAllTags = () => {
    this.setState({
      tags: this.getTags()
    });
  };

  setActionForAdditionalPromptExit(): void {
    const {
      isBulkOfferActive,
      subcampaign
    } = this.props;

    if (this.isModifiedOffer() && (!isDraft(subcampaign) || isBulkOfferActive)) {
      setActionByName(this.promptExitActionNameForSaveAsDraft, this.handleSaveAsDraft);
    }
  }

  pasteCopiedOffer() {
    // Remove hash from url without reloading the page
    const { location } = this.props;
    updateQueryParams(location, { paste: undefined });

    this.handlePasteFromCopiedOffer();
  }

  pickOfferStatePart(): Partial<State> {
    const { offer, deliverables } = this.state;
    const clonedStateSlice = cloneDeep({
      offer,
      deliverables
    });

    // normalize state nulls to empty values or empty arrays
    // in case when it was null before, but then was edited and then undo-ed to empty value
    clonedStateSlice.offer.includesShipping = !!clonedStateSlice.offer.includesShipping;
    clonedStateSlice.offer.perks = clonedStateSlice.offer.perks || '';
    clonedStateSlice.offer.legalRequirements = clonedStateSlice.offer.legalRequirements || '';
    clonedStateSlice.offer.personalMessage = clonedStateSlice.offer.personalMessage || '';
    clonedStateSlice.deliverables = clonedStateSlice.deliverables.map((deliverable) => ({
      ...deliverable,
      description: deliverable.description || '',
      location: deliverable.location || '',
      hashtags: deliverable.hashtags || [],
      mentions: deliverable.mentions || [],
      links: deliverable.links || [],
      tags: deliverable.tags || [],
      trackingLinks: deliverable.trackingLinks || [],
      landingPageUrl: deliverable.landingPageUrl || '',
      landingPageDescription: deliverable.landingPageDescription || ''
    }));

    return clonedStateSlice;
  }

  isModifiedOffer(): boolean {
    return this.state.isModified && !isEqual(this.initialState, this.pickOfferStatePart());
  }

  getDefaultState = (nextProps: Props) => {
    const { offer, offerDeliverables, isMultipleCreatorsSelected } = nextProps;
    const deliverables = offer.isDefault === false && isMultipleCreatorsSelected
      ? []
      : offerDeliverables;

    const updatedDeliverables = deliverables.map(deliverable => {
      const trackingLinks = !isEmpty(deliverable.trackingLinks)
        ? deliverable.trackingLinks
        : [{ baseUrl: '', description: '' }];

      return {
        ...deliverable,
        landingPageUrl: trackingLinks[0].baseUrl || '',
        landingPageDescription: trackingLinks[0].description || ''
      };
    });

    const updatedAdditionalCompensation = {
      voucherAmount: offer.additionalCompensation.voucherAmount ?? '',
      voucherDescription: offer.additionalCompensation.voucherDescription ?? '',
      otherDescription: offer.additionalCompensation.otherDescription ?? ''
    };

    return {
      offer: {
        ...nextProps.offer,
        additionalCompensation: updatedAdditionalCompensation
      },
      deliverables: sortNewDeliverables<DeliverableAlias>(updatedDeliverables || [], DELIVERABLE_SORTING),
      showValidation: false,
      // Changes to initial draft offer are persisted on backend. Other changes are only kept on client
      // side and are lost when leaving the page.
      shouldPersistChangesOnBackend: isDraft(nextProps.subcampaign) && !nextProps.isBulkOfferActive,
      needsRenegotiations: false,
      isModified: false,
      isValidTermsAndConditions: true,
      isValidTypeOfOffer: true,
      controlledComponentRerenderKey: 'default'
    };
  };

  persistOfferChanges = () => {
    const { dispatchPutOffer } = this.props;
    const { offer, deliverables, shouldPersistChangesOnBackend } = this.state;

    this.setOfferTypeForBulkEdit();

    if (shouldPersistChangesOnBackend && this.validateInput(false)) {
      dispatchPutOffer(offer, deliverables);
    }
  };

  setOfferTypeForBulkEdit() {
    const { dispatchSetContentTypes } = this.props;

    dispatchSetContentTypes(
      Array.from(getUniqDeliverableTypes(this.state.deliverables.map(d => d.deliverableType)))
    );
  }

  getBulkOfferSubcampaigns() {
    const { bulkOfferStore } = this.props;

    return getBulkOfferSelectedSubcampaigns(bulkOfferStore);
  }

  getSubmitButtonLabel() {
    const { subcampaign, isBulkOfferActive } = this.props;
    const isModified = this.isModifiedOffer();
    const submitButtonLabel = isDraft(subcampaign)
      ? __t('subcampaign_offer_module.button_send')
      : __t('subcampaign_offer_module.button_resend');

    if (isBulkOfferActive) {
      return __t('subcampaign_offer_module.button_send');
    }

    if (this.canResendOfferEvenWithoutModifications()) {
      return __t('subcampaign_offer_module.button_resend');
    }

    if (!isModified || this.isRequireRenegotiations()) {
      return submitButtonLabel;
    }

    if (this.isTriggeringOfferUpdate()) {
      return __t('subcampaign_offer_module.button_send_offer_update');
    }

    return !isDraft(subcampaign)
      ? __t('subcampaign_offer_module.button_save')
      : submitButtonLabel;
  }

  handleOfferTypeChanged = (changedOfferType?: {
    type: SubcampaignOfferType,
    budget?: number,
    perks?: string
  } | null) => {
    const { offer } = this.state;

    if (!changedOfferType) {
      return;
    }

    const newOffer = {
      ...offer,
      ...EMPTY_OFFER_TYPE,
      ...changedOfferType
    };

    this.setState({
      offer: newOffer,
      isModified: true,
      needsRenegotiations: true
    }, this.persistOfferChanges);
  };

  handleAdditionalCompensation = async (
    data: OfferAdditionalCompensationData
  ): Promise<void> => {
    const { offer } = this.state;
    const voucherAmount = String(data.voucherAmount);

    const newOffer = {
      ...offer,
      additionalCompensation: {
        ...data,
        voucherAmount: !isEmpty(voucherAmount)
          ? Number(data.voucherAmount)
          : ''
      }
    };

    this.setState({
      offer: newOffer,
      isModified: true
    }, () => {
      this.setState(state => ({
        ...state,
        needsRenegotiations: !isEqual(this.initialState, state)
      }), this.persistOfferChanges);
    });
  };

  handleTermsAndConditionsChanged = (newTermsAndConditions?: {
    legalRequirements?: string,
    includesShipping?: boolean
  } | null) => {
    if (newTermsAndConditions) {
      this.setState((state) => ({
        offer: {
          ...state.offer,
          ...newTermsAndConditions
        },
        isModified: true
      }), this.persistOfferChanges);
    }
  };

  handlePersonalMessageChanged = (changedField: any) => {
    const { offer } = this.state;

    const newOffer = {
      ...offer,
      ...changedField
    };

    this.setState({
      offer: newOffer,
      isModified: true
    }, this.persistOfferChanges);
  };

  getDefaultDeliverableType(): DeliverableType {
    const { subcampaignsList, subcampaign } = this.props;
    const { deliverables } = this.state;
    const availableInfluencerChannels = this.getAvailableInfluencerChannels();
    let deliverableType: DeliverableType = DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER;

    const offers = subcampaignsList.reduce((res, s) => {
      if (s.offer) {
        res.push(s.offer);
      }

      if (s.updatedOffer) {
        res.push(s.updatedOffer);
      }

      return res;
    }, [] as Array<SubcampaignOffer>);
    const campaignType = getCampaignContentType(offers);

    if (campaignType === CHANNEL_TYPE_YOUTUBE && availableInfluencerChannels.includes(CHANNEL_TYPE_YOUTUBE)) {
      deliverableType = DELIVERABLE_TYPE_YOUTUBE_VIDEO;
    } else if (campaignType === CHANNEL_TYPE_TIKTOK && availableInfluencerChannels.includes(CHANNEL_TYPE_TIKTOK)) {
      deliverableType = DELIVERABLE_TYPE_TIKTOK_VIDEO;
    } else {
      if (availableInfluencerChannels.length === 1 && availableInfluencerChannels[0] === CHANNEL_TYPE_YOUTUBE) {
        deliverableType = DELIVERABLE_TYPE_YOUTUBE_VIDEO;
      }

      if (availableInfluencerChannels.length === 1 && availableInfluencerChannels[0] === CHANNEL_TYPE_TIKTOK) {
        deliverableType = DELIVERABLE_TYPE_TIKTOK_VIDEO;
      }

      // or fallback to Instagram which is set to default
    }

    if (availableInfluencerChannels.length === 0) {
      deliverableType = DELIVERABLE_TYPE_CONTENT_PRODUCTION;
    }

    const canUseCurrentDefault = canAddDeliverableToSubcampaigns(
      deliverableType,
      deliverables,
      subcampaignsList.filter(s => s.id !== subcampaign.id));

    if (!canUseCurrentDefault) {
      deliverableType = DELIVERABLE_TYPE_CONTENT_PRODUCTION;
    }

    return deliverableType;
  }

  handleDeliverableCreate = () => {
    const { offer, overview } = this.props;

    const newDeliverable: DeliverableAlias = {
      id: generateNewDeliverableId(),
      deliverableType: this.getDefaultDeliverableType(),
      contentApprovalTimeline: {
        startDate: '',
        endDate: ''
      },
      contentGoLiveTimeline: {
        startDate: '',
        endDate: ''
      },
      insightsProvisionTimeline: {
        startDate: '',
        endDate: ''
      },
      startDate: '',
      endDate: '',
      analytics: [],
      insightsCompletenessStatus: 'missing',
      name: '',
      requiresLinks: true,
      offerId: offer.id,
      contentApprovalV2: overview?.contentApprovalV2,
      contentApproval: {
        caption: {
          text: '',
          approvalStatus: 'pending'
        },
        approvalNotes: [],
        assets: []
      },
      captionApprovalRequired: false,
      trackingLinks: [],
      landingPageUrl: '',
      landingPageDescription: ''
    };

    this.setState(state => ({
      deliverables: [...(state.deliverables || []), newDeliverable],
      isModified: true,
      expandedIdsMap: {
        ...state.expandedIdsMap,
        [newDeliverable.id]: true
      },
      tags: this.getTags()
    }), this.persistOfferChanges);
  };

  handleDeliverableAdd = (deliverable: DeliverableAlias) => {
    const newDeliverable = {
      ...deliverable,
      id: generateNewDeliverableId()
    };

    this.setState(state => ({
      deliverables: [...(state.deliverables || []), omit(newDeliverable, 'creatorProfile')],
      isModified: true,
      expandedIdsMap: {
        ...state.expandedIdsMap,
        [newDeliverable.id]: true
      },
      tags: this.getTags()
    }), this.persistOfferChanges);
  };

  handleDeliverableEdit = (deliverable: DeliverableAlias) => {
    this.setState(state => ({
      deliverables: (state.deliverables || []).map(oldDeliverable => {
        const updatedDeliverable = {
          ...deliverable
        };

        return oldDeliverable.id === deliverable.id ? updatedDeliverable : oldDeliverable;
      }),
      isModified: true
    }), this.persistOfferChanges);
  };

  handleDeliverableRemove = (deliverableId: number) => {
    this.setState(state => {
      const updatedDeliverables = (state.deliverables || []).filter(deliverable => deliverable.id !== deliverableId);

      return {
        deliverables: updatedDeliverables,
        isModified: true,
        tags: this.getTags(updatedDeliverables)
      };
    }, this.persistOfferChanges);
  };

  canPasteCopiedOfferDetails = () => {
    const { copiedOfferDetails } = this.props;

    return Boolean(copiedOfferDetails);
  };

  shouldAskForConfirmationBeforeCopying() {
    const { isBulkOfferActive } = this.props;
    const { offer, deliverables } = this.state;

    return !isOfferDraftAndBlank(offer) || deliverables.length > 0 || isBulkOfferActive;
  }

  handlePasteFromCopiedOffer = () => {
    const { copiedOfferDetails } = this.props;

    if (!copiedOfferDetails) {
      return;
    }

    this.handleCopyFromAnotherOffer(copiedOfferDetails.offer, copiedOfferDetails.deliverables);
  };

  confirmAndPasteFromCopiedOffer = () => {
    const { dispatch } = this.props;

    if (this.shouldAskForConfirmationBeforeCopying()) {
      dispatch(showConfirmModal({
        title: __t('subcampaign_offer_copy_paste.confirmation_modal_title'),
        message: __t('subcampaign_offer_copy_paste.confirmation_modal_description'),
        onConfirm: () => {
          this.handlePasteFromCopiedOffer();
          dispatch(hideConfirmModal());
        },
        onCancel: () =>
          dispatch(hideConfirmModal())
      }));
    } else {
      this.handlePasteFromCopiedOffer();
    }
  };

  handleCopyFromAnotherOffer = (offer: SubcampaignOffer, deliverables: Array<DeliverableAlias>) => {
    const {
      subcampaign,
      subcampaignsList,
      isBulkOfferActive,
      isSCP
    } = this.props;

    const originalOffer = subcampaign.updatedOffer || subcampaign.offer;
    const bulkOfferSubcampaigns = this.getBulkOfferSubcampaigns();

    const newOffer = {
      ...offer,
      deliverablesOverview: undefined, // not expected by backend
      id: originalOffer.id,
      state: originalOffer.state,
      personalMessage: getPlainText(offer.personalMessage) // before we had `personalMessage` with markdown (remove this line in 1 month)
    };

    let newDeliverables: Array<any> = filterOutIrrelevantDeliverables(
      deliverables,
      isBulkOfferActive ? bulkOfferSubcampaigns.map(s => s.creatorProfile) : [subcampaign.creatorProfile],
      isSCP
    ).map(d => omit(d, 'id')); // clone deliverable objects;

    const otherSubcampaigns = subcampaignsList.filter(s => s.id !== subcampaign.id);
    newDeliverables = newDeliverables
      .filter(d => canAddDeliverableToSubcampaigns(d.deliverableType, newDeliverables, otherSubcampaigns));

    newDeliverables
      .forEach(d => {
        d.id = generateNewDeliverableId(); // eslint-disable-line no-param-reassign
        d.offerId = newOffer.id; // eslint-disable-line no-param-reassign
        d.trackingLinks = []; // eslint-disable-line no-param-reassign
      });

    this.setState({
      offer: newOffer,
      deliverables: sortDeliverables(newDeliverables),
      showValidation: false,
      isModified: true,
      needsRenegotiations: true,
      controlledComponentRerenderKey: Date.now().toString()
    }, this.persistOfferChanges);
  };

  setOfferSendingState(isSendingOffer: boolean) {
    this.setState({
      isSendingOffer
    });
  }

  handleSaveAsDraft = async (handler?: () => void): Promise<void> => {
    const {
      bulkOfferStore,
      dispatchSaveOffersAsDraft,
      subcampaign,
      dispatchResetBulkOffer
    } = this.props;
    const { offer, deliverables } = this.state;
    const { shortlistedSubcampaigns } = bulkOfferStore;

    const subcampaignIds = this.getBulkOfferSubcampaigns().map(s => s.id);
    const customOfferFields = getCustomOfferFields(
      subcampaignIds, offer, deliverables, bulkOfferStore.customizedFieldsByIdMap
    );
    const offers: Array<SubcampaignOfferForm> = Object.keys(customOfferFields).map(key => {
      const subcampaignId = parseInt(key, 10);
      const fields = customOfferFields[key];
      const budget = !isNil(fields.budget) && fields.budget !== offer.budget
        ? Number(fields.budget)
        : offer.budget;
      const legalRequirements = !isNil(fields.legalRequirements)
        && fields.legalRequirements !== offer.legalRequirements
        ? fields.legalRequirements
        : offer.legalRequirements;
      const personalMessage = !isNil(fields.personalMessage)
        && fields.personalMessage !== offer.personalMessage
        ? fields.personalMessage
        : offer.personalMessage;
      const offerId = shortlistedSubcampaigns.filter(
        camp => camp.id === subcampaignId
      )[0]?.offer.id ?? offer.id;

      return {
        ...offer,
        budget,
        id: offerId,
        legalRequirements: legalRequirements ?? offer.legalRequirements,
        personalMessage: personalMessage ?? offer.personalMessage
      };
    });


    await dispatchSaveOffersAsDraft(offers, deliverables);

    this.setState(
      { isModified: false },
      () => {
        dispatchResetBulkOffer();
        if (typeof handler === 'function') {
          handler();
        } else {
          navigate.to(`/campaigns/${subcampaign.campaign.id}/participants/shortlisted`);
        }
      }
    );
  };

  handleSendOffer = async (): Promise<void> => {
    const { subcampaign, isBulkOfferActive, dispatch } = this.props;
    const { offer } = this.state;
    const isModified = this.isModifiedOffer();
    const isInputValid = this.validateInput(true);
    const quoteRequestMessage = offer.type === SUBCAMPAIGN_OFFER_TYPE_QUOTE ? __t('subcampaign_offer_module.request_quote_confirmation_description') : null;
    const quoteRequestTitle = offer.type === SUBCAMPAIGN_OFFER_TYPE_QUOTE ? __t('subcampaign_offer_module.request_quote_confirmation_title') : null;

    if (!isInputValid) {
      this.setState({
        expandedIdsMap: this.getExpandedIdsMap(true)
      });

      return;
    }

    this.setOfferSendingState(true);

    try {
      if (isBulkOfferActive) {
        await this.confirmAndSendInitialBulkOffer(quoteRequestTitle, quoteRequestMessage);
        return;
      }

      if (isDraft(subcampaign)) {
        await this.confirmAndSendInitialOffer(quoteRequestTitle, quoteRequestMessage);
        return;
      }

      if (this.canResendOfferEvenWithoutModifications()) {
        await this.confirmAndSendRecreatedOffer(quoteRequestTitle, quoteRequestMessage);
        return;
      }

      if (!isModified) {
        dispatch(showConfirmModal({
          message: __t('subcampaign_offer_module.no_changes_made_description'),
          title: __t('subcampaign_offer_module.no_changes_made_title'),
          onConfirm: () => {
            dispatch(hideConfirmModal());
          },
          confirmButtonLabel: __t('subcampaign_offer_module.close_action_button')
        }));
        this.setOfferSendingState(false);
        return;
      }

      if (this.isRequireRenegotiations()) {
        await this.confirmAndSendEditedOffer();
      } else {
        await this.sendEditedOffer();
      }
    } catch (e) {
      logError(e);
      this.setOfferSendingState(false);
    }
  };

  canResendOfferEvenWithoutModifications() {
    return isRevoked(this.props.subcampaign);
  }

  isTriggeringOfferUpdate(): boolean {
    const newOfferState = this.pickOfferStatePart();
    // will pick only fields which can triggers renegotiation and compare them with initial values
    const triggers = ['type', 'budget', 'perks', 'additionalCompensation.voucherAmount'];

    return this.state.needsRenegotiations
      && !isEqual(
        pick(this.initialState.offer, triggers),
        pick(newOfferState.offer, triggers));
  }

  isRequireRenegotiations(): boolean {
    const { subcampaign } = this.props;

    return isAccepted(subcampaign) && this.isTriggeringOfferUpdate();
  }

  isSubmitButtonDisabled() {
    const { isSendingOffer } = this.state;

    return isSendingOffer;
  }

  validateInput = (triggerWarningPopup: boolean = true) => {
    const { dispatch } = this.props;
    const { offer, deliverables } = this.state;
    const isFormValid = Boolean(this.form && this.form.isFormValid());

    const isInputValid = offer.type && !isEmpty(deliverables) && isFormValid;

    if (isInputValid) {
      return true;
    }

    if (triggerWarningPopup) {
      this.setState({
        showValidation: true
      });

      dispatch(danger(__t('subcampaign_offer_module.missing_invalid_fields')));
      scrollToMissingField(true);
    }

    return false;
  };

  confirmAndRemoveDeliverable = (deliverable: DeliverableAlias) => {
    const { dispatch } = this.props;
    const hasApprovedOutfits = (deliverable.outfits ?? [])
      .some(o => DISABLED_OUTFIT_STATUSES.includes(o.status));

    if (hasApprovedOutfits) {
      dispatch(showConfirmModal({
        message: (
          <div className="subcampaign-offer__confirm-modal-message">
            {__t('subcampaign_offer_module.remove_outfits_task_deliverable_description', {
              outfitStatuses: this.getDisabledOutfitStatusString()
            })}
          </div>
        ),
        title: __t('subcampaign_offer_module.remove_outfits_task_deliverable_title'),
        onConfirm: () => dispatch(hideConfirmModal()),
        confirmButtonLabel: __t('collabary_common.action_button.close')
      }));
    } else {
      dispatch(showConfirmModal({
        message: __t('subcampaign_offer_module.remove_deliverable_description'),
        title: __t('subcampaign_offer_module.remove_deliverable_title'),
        onConfirm: () => {
          this.handleDeliverableRemove(deliverable.id);
          dispatch(hideConfirmModal());
        },
        onCancel: () =>
          dispatch(hideConfirmModal())
      }));
    }
  };

  confirmAndDuplicateDeliverable = (deliverable: DeliverableAlias) => {
    const { dispatch } = this.props;

    const parsedDeliverable = {
      ...deliverable,
      requiresLinks: Boolean(deliverable.requiresLinks)
    };

    dispatch(showConfirmModal({
      message: __t('subcampaign_offer_module.duplicate_deliverable_description'),
      title: __t('subcampaign_offer_module.duplicate_deliverable_title'),
      onConfirm: () => {
        this.handleDeliverableAdd(parsedDeliverable);
        dispatch(hideConfirmModal());
      },
      onCancel: () =>
        dispatch(hideConfirmModal())
    }));
  };

  confirmAndSendInitialOffer = (title?: string | null, message?: string | null): Promise<void> => {
    const { dispatch } = this.props;

    return new Promise((resolve) => {
      dispatch(showConfirmModal({
        message: message || CONFIRMATION_DESCRIPTION,
        title: title || __t('subcampaign_offer_module.send_initial_offer_confirmation_title'),
        onConfirm: async () => {
          dispatch(hideConfirmModal());
          await this.sendInitialOffer();
          resolve();
        },
        onCancel: () => {
          dispatch(hideConfirmModal());
          this.setOfferSendingState(false);
          resolve();
        }
      }));
    });
  };

  confirmAndSendEditedOffer = () => {
    const { dispatch } = this.props;

    return new Promise<void>((resolve) => {
      dispatch(showConfirmModal({
        message: __t('subcampaign_offer_module.send_edited_offer_confirmation_description'),
        title: __t('subcampaign_offer_module.send_edited_offer_confirmation_title'),
        onConfirm: async () => {
          dispatch(hideConfirmModal());
          await this.sendEditedOffer();
          resolve();
        },
        onCancel: () => {
          dispatch(hideConfirmModal());
          this.setOfferSendingState(false);
          resolve();
        }
      }));
    });
  };

  confirmAndSendRecreatedOffer = (title?: string | null, message?: string | null): Promise<void> => {
    const { dispatch } = this.props;

    return new Promise((resolve) => {
      dispatch(showConfirmModal({
        message: message || CONFIRMATION_DESCRIPTION,
        title: title || __t('subcampaign_offer_module.send_initial_offer_confirmation_title'),
        onConfirm: async () => {
          dispatch(hideConfirmModal());
          await this.sendRecreatedOffer();
          resolve();
        },
        onCancel: () => {
          dispatch(hideConfirmModal());
          this.setOfferSendingState(false);
          resolve();
        }
      }));
    });
  };

  confirmAndSendInitialBulkOffer = (title?: string | null, message?: string | null): Promise<void> => {
    const { dispatch } = this.props;

    return new Promise((resolve) => {
      dispatch(showConfirmModal({
        message: message || CONFIRMATION_DESCRIPTION,
        title: title || this.getSubmitButtonLabel(),
        onConfirm: async () => {
          dispatch(hideConfirmModal());
          await this.sendInitialBulkOffer();
          resolve();
        },
        onCancel: () => {
          dispatch(hideConfirmModal());
          this.setOfferSendingState(false);
          resolve();
        }
      }));
    });
  };

  sendInitialOffer = async () => {
    const { subcampaign, dispatchPutOffer, dispatchPutOfferState } = this.props;
    const { offer, deliverables } = this.state;

    await dispatchPutOffer(offer, deliverables);

    return dispatchPutOfferState(subcampaign.offer.id, { state: SUBCAMPAIGN_STATE_PENDING })
      .then(() => {
        this.setState({
          isModified: false
        });
      })
      .then(() => navigate.to(`/campaigns/${subcampaign.campaign.id}/subcampaigns/${subcampaign.id}?showOfferCongratsModal=true`));
  };

  sendEditedOffer = (): Promise<void> => {
    const { subcampaign, dispatchPutOffer, dispatch } = this.props;
    const { offer, deliverables } = this.state;

    return dispatchPutOffer(offer, deliverables)
      .then(() => {
        this.setState({
          isModified: false,
          needsRenegotiations: false
        });
      })
      .then(() => navigate.to(`/campaigns/${subcampaign.campaign.id}/subcampaigns/${subcampaign.id}`))
      .catch(error => {
        const errorMsg = this.getErrorMsgByCode(error);

        this.setState({
          isSendingOffer: false
        });

        logError(error);
        dispatch(danger(errorMsg));
      });
  };

  sendRecreatedOffer = async () => {
    const { subcampaign, dispatchPutOffer, dispatchUncancelSubcampaign } = this.props;
    const { offer, deliverables } = this.state;

    await dispatchPutOffer({
      ...offer,
      personalMessage: '' // COLLAB-8963
    }, deliverables);

    if (isCanceled(subcampaign) || isExpired(subcampaign)) {
      await dispatchUncancelSubcampaign(subcampaign);
    }

    this.setState({
      isModified: false
    }, () => navigate.to(`/campaigns/${subcampaign.campaign.id}/subcampaigns/${subcampaign.id}`));
  };

  sendInitialBulkOffer = async (): Promise<void> => {
    const { subcampaign, dispatchPerformBulkOfferCreation, dispatch } = this.props;
    const { offer, deliverables } = this.state;
    const bulkOfferSubcampaigns = this.getBulkOfferSubcampaigns();
    const campaignId = subcampaign.campaign.id;

    try {
      await dispatchPerformBulkOfferCreation(campaignId, bulkOfferSubcampaigns.map(s => s.id), offer, deliverables);

      this.setState({
        isModified: false
      }, () => navigate.to(`/campaigns/${campaignId}/participants/pending`));
    } catch (error) {
      const errorMsg = this.getErrorMsgByCode(error);

      this.setState({
        isSendingOffer: false
      });

      logError(error);
      dispatch(danger(errorMsg));
    }
  };

  getAvailableInfluencerChannels(): Array<string> {
    const { subcampaign, isBulkOfferActive } = this.props;
    const bulkOfferSubcampaigns = this.getBulkOfferSubcampaigns();

    return isBulkOfferActive ?
      getInfluencersAvailableDeliverableChannels(bulkOfferSubcampaigns.map(s => s.creatorProfile)) :
      getInfluencerAvailableDeliverableChannels(subcampaign.creatorProfile);
  }

  getActiveInfluencerProfile(): SubcampaignInfluencerProfile {
    const { isBulkOfferActive, subcampaign } = this.props;
    const bulkOfferSubcampaigns = this.getBulkOfferSubcampaigns();
    if (isBulkOfferActive && bulkOfferSubcampaigns.length === 1) {
      return bulkOfferSubcampaigns[0].creatorProfile;
    }

    return subcampaign.creatorProfile;
  }

  getExpandedIdsMap = (isExpanded: boolean) => this.state.deliverables.reduce((res, deliverable) => {
    res[deliverable.id] = isExpanded;
    return res;
  }, {});

  handleToggleExpanded = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.currentTarget;
    const key = target.dataset.key;

    this.setState(state => ({
      expandedIdsMap: {
        ...state.expandedIdsMap,
        [key || '']: !state.expandedIdsMap[key || '']
      }
    }));
  };

  handleToggleAllExpanded = (type: boolean): void => {
    this.setState(state => ({
      ...state,
      expandedIdsMap: this.getExpandedIdsMap(type)
    }));
  };

  handleBulkOfferFieldCustomized = (fieldName: string) => {
    switch (fieldName) {
      case 'budget':
        this.handleOfferTypeChanged({ type: SUBCAMPAIGN_OFFER_TYPE_OFFER, budget: 0 });
        break;
      case 'legalRequirements':
        this.handleTermsAndConditionsChanged({ legalRequirements: '' });
        break;
      case 'personalMessage':
        this.handlePersonalMessageChanged({ personalMessage: '' });
        break;
      default:
        break;
    }
  };

  renderResizeControl = () => {
    const { expandedIdsMap } = this.state;

    return (
      <SubcampaignResizeControl
        toggleExpanded={this.handleToggleAllExpanded}
        expandedIdsMap={expandedIdsMap}
      />
    );
  };

  renderBar(): React.ReactNode {
    const { subcampaign, brandProfile, isBulkOfferActive, variablePaymentsAmount } = this.props;
    const { offer, deliverables } = this.state;
    const submitButtonLabel = this.getSubmitButtonLabel();
    const bulkOfferSubcampaigns = this.getBulkOfferSubcampaigns();

    return (
      <div className="subcampaign-offer__brand-offer-banner">
        <OfferCardBanner
          key={submitButtonLabel}
          canEdit
          isForBrand
          triggersOfferUpdate={!isDraft(subcampaign) && this.isTriggeringOfferUpdate()}
          influencerProfile={this.getActiveInfluencerProfile()}
          brandProfile={brandProfile}
          subcampaign={subcampaign}
          visibleOffer={offer}
          visibleDeliverables={deliverables}
          variablePaymentsAmount={variablePaymentsAmount}
          isBulkOfferActive={isBulkOfferActive}
          bulkOfferSubcampaigns={bulkOfferSubcampaigns}
          isSubmitButtonDisabled={this.isSubmitButtonDisabled()}
          submitButtonLabel={submitButtonLabel}
          onSendOffer={this.handleSendOffer}
          onSaveAsDraft={this.handleSaveAsDraft}
          onPasteCopiedOfferDetails={this.canPasteCopiedOfferDetails() ? this.confirmAndPasteFromCopiedOffer : undefined}
        />
      </div>
    );
  }

  renderCustomizeButtonForField(fieldName: string) {
    const { offer } = this.state;

    const containerClasses = classnames({
      'subcampaign-offer__customize-button': true,
      [`subcampaign-offer__customize-button--${snakeCase(fieldName).replace('_', '-')}`]: fieldName
    });

    return (
      <div className={containerClasses}>
        <ActionButtonWithBulkCustomizeOffer
          modalContentProps={{
            fieldName,
            bulkOffer: offer,
            onChanged: this.handleBulkOfferFieldCustomized
          }}
          color="secondary"
          width={fieldName === 'budget' ? 'maximum' : 'normal'}
        >
          {__t(`subcampaign_offer_module.button_customize_${snakeCase(fieldName)}`)}
        </ActionButtonWithBulkCustomizeOffer>
      </div>
    );
  }

  renderCustomizeButtonForBudget = () => this.renderCustomizeButtonForField('budget');

  renderCustomizeButtonForLegalRequirements = () => this.renderCustomizeButtonForField('legalRequirements');

  renderCustomizeButtonForPersonalMessage = () => this.renderCustomizeButtonForField('personalMessage');

  render(): React.ReactNode {
    const {
      subcampaign,
      campaignOverview,
      isBulkOfferActive,
      isMultipleCreatorsSelected,
      offer: propOffer,
      overview
    } = this.props;
    const {
      expandedIdsMap,
      offer,
      deliverables,
      showValidation,
      controlledComponentRerenderKey,
      tags
    } = this.state;
    const influencerProfile = this.getActiveInfluencerProfile();
    const isModified = this.isModifiedOffer();
    const metrics = getPriceRecommendation(influencerProfile.channels, 'instagram');
    const bulkOfferSubcampaigns = this.getBulkOfferSubcampaigns();
    const availableInfluencerChannels = this.getAvailableInfluencerChannels();
    const isQuoteProvidedByCreator = isQuoteProvided(subcampaign);

    const deliverablesHeaderClasses = classnames({
      'subcampaign-offer__section-heading': true,
      'subcampaign-offer__section-heading--error': showValidation && isEmpty(deliverables)
    });

    const originalOffer = subcampaign.updatedOffer || subcampaign.offer;
    const initialBudget = originalOffer.budget;

    const hideInfoNote = !isAccepted(subcampaign) || isBulkOfferActive;
    const personalMessageToCreator = __t('subcampaign_offer_module.personal_message_to_creator');

    const filteredDeliverables = this.getFilteredDeliverables();

    const deliverablesForWMCReuse = deliverables
      .filter(d => !isEmpty(d.trackingLinks))
      .map(d => {
        const item = dataGetCommonStructure(String(d.id), d.name, d.deliverableType, false);
        return dataCommonToComponent(item);
      });

    return (
      <React.Fragment>
        {
          isModified && (!isDraft(subcampaign) || isBulkOfferActive) && (
            <PromptExit
              type="page"
              additionalContent={
                isDraft(subcampaign) && !isMultipleCreatorsSelected
                  ?
                  {
                    buttons: [
                      {
                        title: __t('subcampaign_offer_module.button_save_as_draft'),
                        actionName: this.promptExitActionNameForSaveAsDraft,
                        index: 1,
                        color: 'secondary',
                        qaKey: 'confirm-modal__button-save-as-draft'
                      }
                    ]
                  }
                  : undefined
              }
            />
          )
        }
        <Form
          ref={(form?: Form | null): void => {
            this.form = form;
          }}
        >
          <div className="subcampaign-offer">
            <PageHeading
              heading={__t('subcampaign_offer_module.offer_subtitle')}
              size="h3"
              margin="none"
              weight="bold"
              customClass="subcampaign-offer__section-heading"
              forwardedRef={this.headerRef}
            />
            {this.renderBar()}
            <PageHeading
              heading={__t('subcampaign_offer_module.type_of_offer')}
              size="h3"
              margin="none"
              weight="bold"
              customClass="subcampaign-offer__section-heading"
            />
            <PageSection
              padding="medium"
              margin="none"
            >
              <OfferTypeForm
                key={`offer-type-form-${controlledComponentRerenderKey}`}
                type={offer.type}
                perks={offer.perks}
                budget={offer.budget}
                additionalCompensation={offer.additionalCompensation}
                state={offer.state}
                usedBudget={overview?.usedBudget}
                sentOfferType={!isDraft(subcampaign) ? propOffer.type : null}
                onEdit={this.handleOfferTypeChanged}
                initialBudget={initialBudget}
                remainingBudget={campaignOverview && campaignOverview.remainingBudget || 0}
                totalBudget={subcampaign.campaign.budget}
                isCopiedOffer={this.canPasteCopiedOfferDetails()}
                priceRecommendation={{
                  min: !isBulkOfferActive ? (metrics.minPricePerPost || 0) : 0,
                  max: !isBulkOfferActive ? (metrics.maxPricePerPost || 0) : 0
                }}
                hideInfoNote={hideInfoNote}
                isBulkOfferActive={isBulkOfferActive && bulkOfferSubcampaigns.length > 1}
                isQuoteProvidedByCreator={isQuoteProvidedByCreator}
                renderCustomizeButton={this.renderCustomizeButtonForBudget}
                isResendingCancelled={subcampaign.state === 'revoked'}
              />
              {
                offer.type === 'offer' && (
                  <div className="subcampaign-offer__additional-compensation">
                    <OfferAdditionalCompensation
                      key={`offer-additional-compensation--${controlledComponentRerenderKey}`}
                      additionalCompensation={offer.additionalCompensation}
                      offerState={offer.state}
                      onChange={this.handleAdditionalCompensation}
                    />
                  </div>
                )
              }
            </PageSection>

            <PageHeading
              heading={__t('subcampaign_offer_module.shipping')}
              size="h3"
              margin="none"
              weight="bold"
              customClass="subcampaign-offer__section-heading"
            />
            <PageSection
              margin="none"
              padding="none"
            >
              <OfferShippingForm
                key={`offer-shipping-form-${controlledComponentRerenderKey}`}
                includesShipping={offer.includesShipping}
                onEdit={this.handleTermsAndConditionsChanged}
                hideInfoNote={hideInfoNote}
                isBulkOfferActive={isBulkOfferActive}
              />
            </PageSection>

            <PageHeading
              heading={__t('subcampaign_offer_module.legal_requirements')}
              size="h3"
              margin="none"
              weight="bold"
              customClass="subcampaign-offer__section-heading"
            />
            <PageSection
              margin="none"
              padding="none"
            >
              <OfferLegalRequirementsForm
                key={`legal-requirements-form-${controlledComponentRerenderKey}`}
                legalRequirements={offer.legalRequirements}
                onEdit={this.handleTermsAndConditionsChanged}
                hideInfoNote={hideInfoNote}
                isBulkOfferActive={isBulkOfferActive && bulkOfferSubcampaigns.length > 1}
                renderCustomizeButton={this.renderCustomizeButtonForLegalRequirements}
              />
            </PageSection>

            <div className={deliverablesHeaderClasses}>
              {__t('subcampaign_offer_module.deliverables')}
              <FormRequiredMark />
              <div className="subcampaign-offer__section-disclaimer">
                {__t('subcampaign_offer_module.deliverables_disclaimer')}
              </div>
              <div className="subcampaign-offer__control">
                <div className="subcampaign-offer__tag-filter-container">
                  <span className="subcampaign-offer__select-tag">
                    {__t('subcampaign_offer_module.select_tag')}
                  </span>
                  <MixedSelectDropdown
                    items={tags}
                    name="subcampaign-offer-edit-tag-filter"
                    withMulti
                    withColors
                    withCheckboxes
                    withSortable
                    withRemovable
                    placeholder={__t('subcampaign_offer_module.tag_filter_placeholder')}
                    onItemsToggle={this.handleTagFilterChange}
                    onItemsRemove={this.handleTagFilterChange}
                    onItemsRemoveAll={this.handleRemoveAllTags}
                    className="subcampaign-offer__tag-filter"
                  />
                </div>
                {this.renderResizeControl()}
              </div>
            </div>
            {
              filteredDeliverables.map((deliverable, index) => (
                <div
                  key={`subcampaign-offer-deliverable-${index}-${deliverable.id}`}
                  className="subcampaign-offer__deliverable"
                >
                  <DeliverableEdit
                    offerId={offer.id}
                    availableChannels={availableInfluencerChannels}
                    subcampaign={subcampaign}
                    deliverable={deliverable}
                    isExpanded={expandedIdsMap[String(deliverable.id)]}
                    hideInfoNote={hideInfoNote}
                    offerDeliverables={deliverables}
                    deliverablesForWMCReuse={deliverablesForWMCReuse}
                    onRemove={this.confirmAndRemoveDeliverable}
                    onEdit={this.handleDeliverableEdit}
                    onToggleExpanded={this.handleToggleExpanded}
                    onDuplicate={this.confirmAndDuplicateDeliverable}
                    data={{ qa: `subcampaign-offer-deliverable-${deliverable.deliverableType}` }}
                    isV2={overview?.contentApprovalV2}
                  />
                </div>
              ))
            }
            <PageSection
              margin="none"
              customClass="subcampaign-offer__add-deliverable"
            >
              <ActionButton
                onClick={this.handleDeliverableCreate}
                data={{ qa: 'subcampaign-offer__add-deliverable' }}
              >
                <div className="subcampaign-offer__add-deliverable-button">
                  <span>
                    {
                      (deliverables || []).length === 0
                        ? __t('subcampaign_offer_module.create_first_deliverable_label')
                        : __t('subcampaign_offer_module.create_another_deliverable_label')
                    }
                  </span>
                </div>
              </ActionButton>
            </PageSection>
            {
              showValidation && isEmpty(deliverables) && (
                <div className="subcampaign-offer__required-field-label form-field__error">
                  {__t('subcampaign_offer_module.label_required_field')}
                </div>
              )
            }
            {
              (isDraft(subcampaign) || isBulkOfferActive) && (
                <React.Fragment>
                  <PageHeading
                    heading={personalMessageToCreator}
                    size="h3"
                    margin="none"
                    weight="bold"
                    customClass="subcampaign-offer__section-heading"
                  />
                  <PageSection
                    margin="none"
                    padding="none"
                  >
                    <OfferPersonalMessageForm
                      key={`personal-message-form-${controlledComponentRerenderKey}`}
                      personalMessage={offer.personalMessage}
                      onEdit={this.handlePersonalMessageChanged}
                      hideInfoNote={hideInfoNote}
                      isBulkOfferActive={isBulkOfferActive && bulkOfferSubcampaigns.length > 1}
                      renderCustomizeButton={this.renderCustomizeButtonForPersonalMessage}
                    />
                  </PageSection>
                </React.Fragment>
              )
            }
          </div>
        </Form>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (
  {
    account,
    bulkOffer,
    router,
    offerDetailsBuffer,
    campaignSubcampaigns,
    subcampaignVariablePayments,
    campaignOverview,
    runtimeConfig
  },
  selfProps: OwnProps
): StateProps => ({
  isPasteOfferActive: get(router, 'location.query.paste') === 'active',
  isBulkOfferActive: bulkOffer.isActive && Boolean(selfProps.isBulkOfferAllowed),
  bulkOfferStore: bulkOffer,
  copiedOfferDetails: offerDetailsBuffer.copiedOfferDetails,
  location: router.location,
  subcampaignsList: campaignSubcampaigns.list,
  variablePaymentsAmount: subcampaignVariablePayments.variablePayments.totalAmount || 0,
  overview: campaignOverview.overview,
  isSCP: isInfluencerRealm(account.roles)
    && runtimeConfig.config.zalandoOutfitTaskTypeEnabled
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchPutOffer: (offer, deliverables) => dispatch(putOffer(offer, deliverables)),
  dispatchSaveOffersAsDraft: (offers, deliverables) => dispatch(saveOffersAsDraft(offers, deliverables)),
  dispatchPutOfferState: (offerId: number, offerState: SubcampaignOfferState) => dispatch(putOfferState(offerId, offerState)),
  dispatchUncancelSubcampaign: (subcampaign: Subcampaign) => dispatch(uncancelSubcampaign(subcampaign)),

  dispatchPerformBulkOfferCreation: (campaignId: number, subcampaignIds: Array<number>, offer: SubcampaignOfferForm, deliverables: Array<DeliverableAlias>) =>
    dispatch(performBulkOfferCreation(campaignId, subcampaignIds, offer, deliverables)),

  dispatchFetchSubcampaigns: (campaignId: number) =>
    dispatch(fetchSubcampaigns(campaignId, { showLoadingComponent: false, includeDeliverablesOverview: true })),

  dispatchResetSubcampaigns: () => dispatch(resetSubcampaigns()),
  dispatchSetContentTypes: (types: Array<string>) => dispatch(setBulkOfferContentTypes(types)),
  dispatchResetBulkOffer: () => dispatch(resetBulkOffer()),
  dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(SubcampaignOfferEdit);
